import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  Autocomplete,
  InputAdornment,
  Select,
  MenuItem,
  OutlinedInput,
  Link,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker, LocalizationProvider } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { useForm, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { modalBoxStyle } from "../styles";
import useEstablishmentUoMs from "../../../hooks/useEstablishmentUoMs";
import inventoryClient from "../../../clients/inventoryClient";
import { useMutation, useQueryClient } from "react-query";
import {
  productSizeTypes,
  unitOfMeasurementTypes,
} from "../../../clients/inventoryObjects";

const servingTypeObjects = [
  { displayText: "Volumetric", value: productSizeTypes.Volumetric },
  { displayText: "Mass", value: productSizeTypes.Mass },
  { displayText: "Count", value: productSizeTypes.Arbitrary },
];

const AddWasteLogModal = ({
  isOpen,
  toggleModalOpen,
  existingWasteLog = null,
  onSuccess,
}) => {
  const { establishmentid } = useParams();
  const queryClient = useQueryClient();

  const [dateValue, setDateValue] = useState(new Date());
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [servingType, setServingType] = useState(productSizeTypes.Volumetric);
  const [uomOptions, setUomOptions] = useState([]);
  const [quantity, setQuantity] = useState("");
  const [selectedUoM, setSelectedUoM] = useState("");

  const debounceTimerRef = useRef(null);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      wasteDate: new Date(),
      reason: "",
    },
  });

  // Debounce search input
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput]);

  // Fetch search results when debounced search changes
  useEffect(() => {
    const fetchResults = async () => {
      if (!debouncedSearch || debouncedSearch.length < 2) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await inventoryClient.searchProductFormatsSlim(
          establishmentid,
          debouncedSearch
        );
        setSearchResults(response.results || []);
      } catch (error) {
        console.error("Error searching product formats:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    fetchResults();
  }, [debouncedSearch, establishmentid]);

  const { data: uomData } = useEstablishmentUoMs(establishmentid);
  const unitsOfMeasurement = useMemo(() => {
    return uomData?.unitsOfMeasurement || [];
  }, [uomData]);

  // Update form when existingWasteLog changes
  useEffect(() => {
    // Only run when modal is actually open
    if (!isOpen) return;

    if (existingWasteLog) {
      setDateValue(
        existingWasteLog.wasteDate
          ? new Date(existingWasteLog.wasteDate)
          : new Date()
      );
      setSelectedProduct({
        productId: existingWasteLog.productId,
        productFormatId: existingWasteLog.productFormatId,
        displayName: existingWasteLog.productName
          ? `${existingWasteLog.productName} (${existingWasteLog.formatName || ""})`
          : "",
        productName: existingWasteLog.productName,
        formatName: existingWasteLog.formatName,
      });
      setServingType(existingWasteLog.quantity?.type || productSizeTypes.Volumetric);
      setQuantity(existingWasteLog.quantity?.amount || "");
      setSelectedUoM(existingWasteLog.quantity?.unitOfMeasurement?.id || "");
      reset({
        wasteDate: existingWasteLog.wasteDate
          ? new Date(existingWasteLog.wasteDate)
          : new Date(),
        reason: existingWasteLog.reason || "",
      });
    } else {
      // Reset for new log
      setDateValue(new Date());
      setSelectedProduct(null);
      setServingType(productSizeTypes.Volumetric);
      setQuantity("");
      setSelectedUoM("");
      reset({
        wasteDate: new Date(),
        reason: "",
      });
    }
  }, [existingWasteLog, isOpen]);

  // Filter UoMs based on serving type
  useEffect(() => {
    const filterUomOptions = (type) => {
      switch (type) {
        case productSizeTypes.Volumetric:
          return unitsOfMeasurement.filter(
            (x) => x.type === unitOfMeasurementTypes.Volumetric && !x.isKegSize
          );
        case productSizeTypes.Mass:
          return unitsOfMeasurement.filter(
            (x) => x.type === unitOfMeasurementTypes.Mass
          );
        case productSizeTypes.Arbitrary:
          return unitsOfMeasurement.filter(
            (x) => x.type === unitOfMeasurementTypes.Arbitrary
          );
        default:
          return unitsOfMeasurement.filter(
            (x) => x.type === unitOfMeasurementTypes.Volumetric && !x.isKegSize
          );
      }
    };

    setUomOptions(filterUomOptions(servingType));
  }, [servingType, unitsOfMeasurement]);

  const createMutation = useMutation(
    (payload) => inventoryClient.createWasteLog(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["waste-logs-infinite"]);
        handleClose();
        if (onSuccess) onSuccess();
      },
    }
  );

  const updateMutation = useMutation(
    (payload) => inventoryClient.updateWasteLog(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["waste-logs-infinite"]);
        handleClose();
        if (onSuccess) onSuccess();
      },
    }
  );

  const handleClose = () => {
    reset();
    setDateValue(new Date());
    setSearchInput("");
    setDebouncedSearch("");
    setSearchResults([]);
    setSelectedProduct(null);
    setServingType(productSizeTypes.Volumetric);
    setQuantity("");
    setSelectedUoM("");
    toggleModalOpen();
  };

  const handleProductSelect = (product) => {
    if (product) {
      setSelectedProduct({
        productId: product.productId,
        productFormatId: product.productFormatId,
        displayName: product.displayName,
        productName: product.productName,
        formatName: product.formatName,
      });
      // Set serving type based on product's default size type, or default to Volumetric
      const defaultType = product.defaultSizeType || productSizeTypes.Volumetric;
      setServingType(defaultType);
      setSearchInput("");
      setSearchResults([]);
      // Reset quantity and UoM when product changes
      setQuantity("");
      setSelectedUoM("");
    }
  };

  const handleTypeChange = (e) => {
    if (e.target.value === servingType) {
      return;
    }
    setServingType(e.target.value);
    // Reset UoM when type changes to ensure it matches the new type
    setSelectedUoM("");
  };

  const handleRemoveProduct = () => {
    setSelectedProduct(null);
    setServingType(productSizeTypes.Volumetric);
    setQuantity("");
    setSelectedUoM("");
  };

  const onSubmit = (data) => {
    if (!selectedProduct || !quantity || (!selectedUoM && servingType !== productSizeTypes.Arbitrary)) {
      return;
    }

    const selectedUoMObject = uomOptions.find(
      (uom) => uom.id === selectedUoM
    );

    const payload = {
      establishmentId: establishmentid,
      productId: selectedProduct.productId,
      productFormatId: selectedProduct.productFormatId,
      wasteDate: dateValue.toISOString(),
      reason: data.reason,
      quantity: {
        amount: parseFloat(quantity),
        type: servingType,
        unitOfMeasurement: selectedUoMObject,
      },
    };

    if (existingWasteLog) {
      updateMutation.mutate({
        ...payload,
        wasteLogId: existingWasteLog.id,
      });
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Modal open={isOpen} onClose={handleClose} aria-labelledby="add-waste-log-modal">
      <Box sx={modalBoxStyle}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 6,
            }}
          >
            <Typography variant="h5">
              {existingWasteLog ? "Edit Waste Log" : "Add New Log"}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseOutlinedIcon />
            </IconButton>
          </Box>

          {/* Date Picker */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="body2" sx={{ marginBottom: 2 }}>
              Date of Log
            </Typography>
            <Controller
              control={control}
              name="wasteDate"
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    dateFormat="mm/dd/yyyy"
                    placeholder="mm/dd/yyyy"
                    onChange={(e) => {
                      field.onChange(e);
                      setDateValue(e);
                    }}
                    value={dateValue}
                    renderInput={(params) => (
                      <TextField
                        size="small"
                        fullWidth
                        {...params}
                        sx={{
                          "& legend": { display: "none" },
                          "& fieldset": { top: 0 },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              )}
            />
          </Box>

          {/* Reason Field */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="body2" sx={{ marginBottom: 2 }}>
              Reason For Waste
            </Typography>
            <Controller
              control={control}
              name="reason"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  placeholder="Enter reason..."
                  sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                  }}
                />
              )}
            />
          </Box>

          {/* Product Format Search */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="body2" sx={{ marginBottom: 2 }}>
              Search Product Formats
            </Typography>
            <Autocomplete
              options={searchResults}
              getOptionLabel={(option) => option.displayName || ""}
              inputValue={searchInput}
              onInputChange={(event, newInputValue) => {
                setSearchInput(newInputValue);
              }}
              onChange={(event, newValue) => {
                handleProductSelect(newValue);
              }}
              loading={isSearching}
              noOptionsText={
                searchInput.length < 2
                  ? "Type at least 2 characters to search"
                  : "No products found"
              }
              value={null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="Search product formats"
                  sx={{
                    "& legend": { display: "none" },
                    "& fieldset": { top: 0 },
                    "& .MuiOutlinedInput-root": {
                      background: (theme) => theme.palette.pure.white,
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start" sx={{ marginLeft: -0.5 }}>
                          <SearchIcon />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          {/* Log Details Section */}
          <Box sx={{ marginBottom: 4 }}>
            <Typography variant="body2" sx={{ marginBottom: 2, fontWeight: 600 }}>
              Log Details
            </Typography>
            <Box
              sx={{
                minHeight: "150px",
                backgroundColor: (theme) => theme.palette.terrain[300],
                borderRadius: 1,
                padding: 2,
              }}
            >
              {selectedProduct ? (
                <Box>
                  {/* Product Name Header */}
                  <Box
                    sx={{
                      backgroundColor: (theme) => theme.palette.terrain[50],
                      padding: 2,
                      borderRadius: 1,
                      marginBottom: 2,
                    }}
                  >
                    <Typography variant="body1" fontWeight={600}>
                      {selectedProduct.productName}
                    </Typography>
                    {selectedProduct.formatName && (
                      <Typography
                        variant="body2"
                        sx={{ color: (theme) => theme.palette.primary.main }}
                      >
                        {selectedProduct.formatName}
                      </Typography>
                    )}
                  </Box>

                  {/* Sizing Type */}
                  <Box sx={{ marginBottom: 2 }}>
                    <Typography variant="body2" sx={{ marginBottom: 1 }}>
                      Sizing Type
                    </Typography>
                    <Select
                      value={servingType}
                      onChange={handleTypeChange}
                      size="small"
                      fullWidth
                      input={
                        <OutlinedInput
                          notched={false}
                          sx={{
                            "& legend": { display: "none" },
                            backgroundColor: (theme) => theme.palette.pure.white,
                          }}
                        />
                      }
                    >
                      {servingTypeObjects.map((x) => (
                        <MenuItem
                          key={x.value}
                          value={x.value}
                          sx={{
                            "&:hover": {
                              color: (theme) => theme.palette.pure.white,
                              backgroundColor: (theme) => theme.palette.terrain[500],
                            },
                          }}
                        >
                          {x.displayText}
                        </MenuItem>
                      ))}
                    </Select>
                  </Box>

                  {/* Quantity and UoM */}
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      alignItems: "flex-start",
                      marginBottom: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ marginBottom: 1 }}>
                        Quantity
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        placeholder="0"
                        fullWidth
                        sx={{
                          "& legend": { display: "none" },
                          "& fieldset": { top: 0 },
                          backgroundColor: (theme) => theme.palette.pure.white,
                        }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ marginBottom: 1 }}>
                        Unit of Measurement
                      </Typography>
                      <Select
                        value={selectedUoM}
                        onChange={(e) => setSelectedUoM(e.target.value)}
                        displayEmpty
                        size="small"
                        fullWidth
                        input={
                          <OutlinedInput
                            notched={false}
                            sx={{
                              "& legend": { display: "none" },
                              backgroundColor: (theme) => theme.palette.pure.white,
                            }}
                          />
                        }
                        renderValue={(value) => {
                          if (!value) return "Select...";
                          const uom = uomOptions.find(
                            (u) => u.id === value
                          );
                          return uom?.name || "Select...";
                        }}
                      >
                        {uomOptions.map((uom) => (
                          <MenuItem key={uom.id} value={uom.id}>
                            {uom.isKegSize ? uom.kegName : uom.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>
                  </Box>

                  {/* Remove Link */}
                  <Box sx={{ textAlign: "right" }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={handleRemoveProduct}
                      sx={{ color: (theme) => theme.palette.primary.main }}
                    >
                      Remove
                    </Link>
                  </Box>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100px",
                  }}
                >
                  <Typography variant="body1" fontWeight={600}>
                    You have not selected any products yet...
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Submit Button */}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={
              !selectedProduct ||
              !quantity ||
              (!selectedUoM && servingType !== productSizeTypes.Arbitrary) ||
              createMutation.isLoading ||
              updateMutation.isLoading
            }
            sx={{ height: 48, textTransform: "none" }}
          >
            {existingWasteLog ? "Update Log" : "Add Log"}
          </Button>
        </form>
      </Box>
    </Modal>
  );
};

export default AddWasteLogModal;
